import React from "react";
import { PublicSiteFooter, PublicSiteHeader } from "../components/PublicSiteChrome";

const updatedOn = "October 4, 2026";
const pageSummaries = {
  privacy: ["Information we collect", "Driver Deliveries app", "How we use information", "Your choices"],
  terms: ["Service use", "Pharmacy & delivery", "Driver Deliveries app", "Medical information"],
  support: ["Customer support", "Driver support", "Medical emergencies"],
};

const PharmacyDetails = () => (
  <section className="legal-pharmacy-details">
    <h2>Pharmacy & legal information</h2>
    <p>
      <strong>Nevo Pharm Ltd.</strong>
      <br />
      Allenby 28, Tel Aviv, Israel
      <br />
      Phone: <a href="tel:+97235550142">+972 3-555-0142</a>
      <br />
      Email:{" "}
      <a href="mailto:contact@nevo-pharm.co.il">contact@nevo-pharm.co.il</a>
      <br />
      Responsible pharmacist: Victoria Ben Younes
      <br />
      License #PH-48213
    </p>
  </section>
);

const Layout = ({ title, page, children }) => (
  <main className="legal-page">
    <PublicSiteHeader active={page} />
    <section className="legal-hero"><div><p className="legal-kicker">Kare Pharm · Operated by Nevo Pharm Ltd.</p><h1>{title}</h1><p>Clear information about how we provide safe pharmacy, delivery, and digital services.</p></div><div className="legal-hero-mark"><i className="bi bi-shield-check" /><span>Trusted care<br />and delivery</span></div></section>
    <article className="legal-content"><aside className="legal-sidebar"><span className="legal-sidebar-label">In this document</span>{(pageSummaries[page] || []).map((section, index) => <div key={section}><b>{String(index + 1).padStart(2, "0")}</b><span>{section}</span></div>)}<small><i className="bi bi-shield-check" /> Official Nevo Pharm policy</small></aside><div className="legal-document-shell"><div className="legal-document-meta"><span><i className="bi bi-calendar3" /> Last updated {updatedOn}</span><span><i className="bi bi-file-earmark-text" /> Official policy document</span></div><div className="legal-document"><div className="legal-document-intro"><i className="bi bi-file-earmark-lock2" /><p>{page === "privacy" ? "Your privacy and health information are handled with care." : page === "terms" ? "Please read these terms before using Kare Pharm services." : "Support information for Kare Pharm customers and delivery drivers."}</p></div>{children}<PharmacyDetails /></div></div></article>
    <PublicSiteFooter />
  </main>
);

export const PrivacyPolicy = () => (
  <Layout title="Privacy Policy" page="privacy">
    <p>
      This Privacy Policy explains how Nevo Pharm Ltd. collects, uses, protects,
      and shares information when you use the Kare Pharm customer app, the Kare
      Driver Deliveries app, and related services.
    </p>
    <h2>Information we collect</h2>
    <p>
      We collect information needed to provide pharmacy, prescription, delivery,
      and customer-support services. This may include account details, contact
      details, delivery address, order history, prescription-related information
      you provide, and health questionnaire responses.
    </p>
    <h2>Driver Deliveries app</h2>
    <p>
      For authorised delivery drivers, the Driver Deliveries app processes
      driver account details, assigned-order details, delivery status, and
      location information while the app is used for an active delivery.
      Location information is used to support delivery coordination, live
      delivery status, and safety. The app does not collect or expose customer
      delivery OTPs to administrators.
    </p>
    <h2>How we use information</h2>
    <p>
      We use information to process and deliver orders, support prescription
      fulfilment, communicate about an order or account, improve service
      reliability, prevent fraud, and meet legal and regulatory obligations.
    </p>
    <h2>Sharing</h2>
    <p>
      Information is shared only as needed with authorised pharmacy staff,
      assigned delivery providers, service providers, and authorities when
      required by law. We do not sell personal information.
    </p>
    <h2>Data protection and retention</h2>
    <p>
      We apply appropriate technical and organisational safeguards to protect
      information. We retain information only for as long as necessary to
      provide services, resolve disputes, comply with legal obligations, and
      enforce agreements.
    </p>
    <h2>Your choices</h2>
    <p>
      You may request access, correction, deletion, or information about your
      personal data, subject to applicable law and recordkeeping requirements.
      Contact us using the details below.
    </p>
    <h2>Contact</h2>
    <p>
      For privacy questions or requests, email{" "}
      <a href="mailto:contact@nevo-pharm.co.il">contact@nevo-pharm.co.il</a>.
    </p>
    <h2>Changes to this policy</h2>
    <p>
      We may update this policy from time to time. The current version will
      always be available at this URL.
    </p>
  </Layout>
);

export const TermsAndConditions = () => (
  <Layout title="Terms & Conditions" page="terms">
    <p>
      These Terms & Conditions govern use of the Kare Pharm customer app, the
      Kare Driver Deliveries app, and related services operated by Nevo Pharm
      Ltd. By using a service, you agree to these terms.
    </p>
    <h2>Service use</h2>
    <p>
      You must provide accurate account, delivery, prescription, and health
      information. You are responsible for keeping your account credentials
      confidential and for activity carried out through your account.
    </p>
    <h2>Pharmacy and delivery services</h2>
    <p>
      Orders, prescriptions, availability, pricing, delivery timing, and
      fulfilment are subject to pharmacist review, applicable law, and service
      availability. Delivery may require identity or other verification where
      permitted or required.
    </p>
    <h2>Driver Deliveries app</h2>
    <p>
      The Driver Deliveries app is for authorised drivers only. Drivers may
      access only orders assigned to them and must use customer and order
      information solely to complete an assigned delivery. Drivers must keep
      devices and credentials secure, follow delivery instructions, comply with
      applicable traffic and safety laws, and complete delivery confirmation
      through the authorised app flow.
    </p>
    <h2>Medical information</h2>
    <p>
      Kare Pharm does not replace professional medical advice, diagnosis, or
      treatment. For urgent medical concerns, contact a qualified healthcare
      professional or emergency service.
    </p>
    <h2>Payments and cancellations</h2>
    <p>
      Charges, cancellations, refunds, and order changes are handled according
      to the information presented during checkout and applicable law. Contact
      support if you need help with an order.
    </p>
    <h2>Acceptable use</h2>
    <p>
      You may not misuse the service, interfere with its operation, attempt
      unauthorised access, or use it in violation of law or another person’s
      rights.
    </p>
    <h2>Changes and contact</h2>
    <p>
      We may update these terms as our services change. For questions, email{" "}
      <a href="mailto:contact@nevo-pharm.co.il">contact@nevo-pharm.co.il</a>.
    </p>
  </Layout>
);

export const Support = () => (
  <Layout title="Support" page="support">
    <p>
      Need help with your Kare Pharm account, order, prescription request,
      customer delivery, or Driver Deliveries app account?
    </p>
    <h2>Contact support</h2>
    <p>
      Email us at{" "}
      <a href="mailto:contact@nevo-pharm.co.il">contact@nevo-pharm.co.il</a> or
      call <a href="tel:+97235550142">+972 3-555-0142</a>. Please do not include
      passwords or other sensitive information in email.
    </p>
    <h2>Driver support</h2>
    <p>
      Drivers should contact support for assignment, delivery-status,
      account-access, or app-location issues. Customer delivery OTPs must only
      be handled through the authorised delivery flow.
    </p>
    <h2>Medical emergencies</h2>
    <p>
      For urgent medical concerns, contact your healthcare provider or local
      emergency services.
    </p>
  </Layout>
);
